/**
 * @file     analytics.ts
 * @purpose  Owner-listing analytics (design sections 10 to 15 Events panels):
 *           the add-only event names and one hook that tags every event with
 *           listing_type and listing_source so owner listings never blend
 *           with MLS® events in reports.
 */
'use client';

import { useCallback } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';

export const OWNER_LISTING_EVENTS = {
  rentEstimateStarted: 'rent_estimate_started',
  rentEstimateShown: 'rent_estimate_shown',
  ownerEntryClick: 'owner_entry_click',
  draftCreated: 'listing_draft_created',
  draftSaved: 'listing_draft_saved',
  draftResumed: 'listing_draft_resumed',
  stepViewed: 'listing_step_viewed',
  stepCompleted: 'listing_step_completed',
  photoUploaded: 'listing_photo_uploaded',
  photoFailed: 'listing_photo_failed',
  fairHousingFlag: 'listing_fair_housing_flag',
  fairHousingFixApplied: 'listing_fair_housing_fix_applied',
  writingHelpUsed: 'listing_writing_help_used',
  mlsDuplicate: 'listing_mls_duplicate',
  phoneCodeSent: 'owner_phone_code_sent',
  phoneVerified: 'owner_phone_verified',
  attested: 'owner_attested',
  submitted: 'listing_submitted',
  editRequested: 'listing_edit_requested',
  paused: 'listing_paused',
  markedLeased: 'listing_marked_leased',
  relisted: 'listing_relisted',
  withdrawn: 'listing_withdrawn',
  keepOrSellClick: 'owner_keep_or_sell_click',
} as const;

export type OwnerListingEvent = (typeof OWNER_LISTING_EVENTS)[keyof typeof OWNER_LISTING_EVENTS];

/** listing_type follows the kind: rentals are 'lease', every other kind is a sale. */
export function listingTypeForKind(kind: string): 'lease' | 'sale' {
  return kind === 'lease' ? 'lease' : 'sale';
}

export function useOwnerListingAnalytics() {
  const { trackEvent, trackCtaClick } = useAnalytics();
  const track = useCallback(
    (eventName: OwnerListingEvent, metadata: Record<string, unknown> = {}, kind = 'lease') => {
      trackEvent({
        eventName,
        eventType: 'funnel',
        category: 'owner_listing',
        label: eventName,
        metadata: { listing_type: listingTypeForKind(kind), listing_source: 'owner', kind, ...metadata },
      });
    },
    [trackEvent],
  );
  return { track, trackCtaClick };
}
